import { Component, OnInit, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router, ActivatedRoute } from '@angular/router';
import { EMPTY } from 'rxjs';
import { filter, take, switchMap } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';

import { AuthStateService } from '../../services/auth-state.service';
import { isSafeInternalTarget } from '../../shared/utils/safe-navigation.util';

@Component({
  selector: 'app-callback',
  template: `
    <div class="flex align-items-center justify-content-center min-h-screen">
      <div class="text-center">
        <i class="pi pi-spin pi-spinner text-4xl text-primary mb-3"></i>
        <p class="text-600">Completamento autenticazione...</p>
      </div>
    </div>
  `,
})
export class CallbackComponent implements OnInit {
  private readonly authState = inject(AuthStateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly logger = inject(NGXLogger);

  // Bridge signal -> Observable creato in un injection context valido (field initializer).
  private readonly isAuthenticated$ = toObservable(this.authState.isAuthenticated);
  private readonly user$ = toObservable(this.authState.user);

  ngOnInit(): void {
    this.isAuthenticated$
      .pipe(
        // Il signal parte da `null` (stato non ancora determinato): aspettiamo un valore booleano definitivo.
        filter((isAuthenticated): isAuthenticated is boolean => isAuthenticated !== null),
        take(1),
        switchMap((isAuthenticated) => {
          if (!isAuthenticated) {
            this.logger.warn('[CallbackComponent] Autenticazione fallita, redirect alla home');
            this.router.navigate(['/']);
            return EMPTY;
          }

          this.logger.info('[CallbackComponent] Utente autenticato, attendo profilo utente');
          return this.user$.pipe(
            filter((user): user is NonNullable<typeof user> => !!user),
            take(1),
          );
        }),
      )
      .subscribe({
        next: (user) => {
          this.logger.info('[CallbackComponent] Dati utente ricevuti', { sub: user.sub });
          this.navigateToTarget();
        },
        error: (error) => {
          this.logger.error('[CallbackComponent] Errore nel flusso di autenticazione', error);
          this.router.navigate(['/']);
        },
      });
  }

  private navigateToTarget(): void {
    let target: string = '/';
    const appStateParam = this.route.snapshot.queryParams['appState'];

    if (appStateParam) {
      try {
        target = JSON.parse(appStateParam)?.target ?? '/';
      } catch (error) {
        this.logger.warn('[CallbackComponent] appState non valido, uso target di default', error);
        target = '/';
      }
    }

    if (!isSafeInternalTarget(target)) {
      this.logger.warn('[CallbackComponent] Target non sicuro rilevato, redirect alla home', target);
      target = '/';
    }

    this.logger.info('[CallbackComponent] Navigazione verso', target);

    // Se il target contiene query params (es: /?tags=...), usa navigateByUrl,
    // altrimenti usa navigate.
    if (target.includes('?')) {
      this.router.navigateByUrl(target);
    } else {
      this.router.navigate([target]);
    }
  }
}